import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Save, Loader2 } from 'lucide-react';
import ImageEditor from '../../components/image-studio/ImageEditor';
import { imageStudioService } from '../../services/imageStudioService';
import { GeneratedImage } from '../../types/imageStudio';
import toast from 'react-hot-toast';

const ImageEditorPage: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [image, setImage] = useState<GeneratedImage | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // Após "remover fundo" o editor troca a camada internamente (sem remontar);
  // aqui só guardamos o id do registro mais recente para o save versionar certo.
  const latestImageIdRef = React.useRef<string | null>(null);

  useEffect(() => {
    if (id) {
      loadImage(id);
    } else {
      setLoading(false);
    }
  }, [id]);

  const loadImage = async (imageId: string) => {
    try {
      const response = await imageStudioService.getImage(imageId);
      if (response.success && response.data) {
        setImage(response.data);
      }
    } catch {
      toast.error(t('imageStudio.loadError', 'Failed to load image'));
      navigate('/image-studio');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (dataUrl: string, width: number, height: number, mimeType: string) => {
    if (!image) return;
    setSaving(true);

    try {
      // Convert data URL to blob
      const res = await fetch(dataUrl);
      const blob = await res.blob();
      const file = new File([blob], `edited-${Date.now()}.${mimeType === 'image/jpeg' ? 'jpg' : 'png'}`, { type: mimeType });

      const response = await imageStudioService.saveEdited(latestImageIdRef.current || image.id, {
        file,
        width,
        height,
        mime_type: mimeType,
        title: image.title
      });

      if (response.success) {
        toast.success(t('imageStudio.saved', 'Image saved!'));
        navigate('/image-studio');
      }
    } catch {
      toast.error(t('imageStudio.saveError', 'Failed to save'));
    } finally {
      setSaving(false);
    }
  };

  const handleBgRemoved = (newImage: GeneratedImage) => {
    // NÃO usar setImage aqui: remontaria o ImageEditor e perderia camadas não salvas
    latestImageIdRef.current = newImage.id;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
      </div>
    );
  }

  if (!image) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <p className="text-gray-500">{t('imageStudio.notFound', 'Image not found')}</p>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-gray-50 dark:bg-gray-900">
      {/* Top Bar */}
      <div className="flex items-center justify-between gap-2 px-3 sm:px-4 py-2 sm:py-3 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
          <button
            onClick={() => navigate('/image-studio')}
            aria-label={t('common.back', 'Voltar')}
            className="flex-shrink-0 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600 dark:text-gray-300" />
          </button>
          <h1 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white truncate max-w-md min-w-0">
            {image.title || t('imageStudio.editor.title', 'Image Editor')}
          </h1>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          {saving && (
            <div className="flex items-center gap-2 text-sm text-primary-600 whitespace-nowrap">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span className="hidden sm:inline">{t('imageStudio.saving', 'Saving...')}</span>
            </div>
          )}
          <button
            onClick={() => navigate('/image-studio')}
            className="px-3 sm:px-4 py-2 rounded-lg text-sm whitespace-nowrap text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            {t('common.cancel', 'Cancel')}
          </button>
        </div>
      </div>

      {/* Editor */}
      <div className="flex-1 overflow-hidden">
        <ImageEditor
          image={image}
          onSave={handleSave}
          onBgRemoved={handleBgRemoved}
        />
      </div>
    </div>
  );
};

export default ImageEditorPage;
